import React, {memo, useCallback, useRef} from 'react';
import cx from 'classnames';
import {isAllowedVideoFormat} from '~/common/fabric/utils';
import {Video} from '~library/atoms/Video';
import {SparkleIcon} from '~library/svg/SparkleIcon';
import {Button} from '~library/atoms/Button';
import {Typography} from '~library/atoms/Typography';
import {VideoAlphaChannel} from '~library/atoms/VideoAlphaChannel';
import './IconUnit.sass';

export const IconUnit = memo(
  ({
    icon,
    height = 32,
    width = 32,
    onClick,
    square = false,
    isActive,
    id = null,
    disabled = false,
    mobile = false,
    hideAnimatedIndicator = false,
    outline = false,
    loading = 'lazy',
    flipEnvelope = null,
    showEnvelopeFront = null,
  }) => {
    // We are updating the dom using refs instead of the usual
    // react rendering method because we are seeing slow performance
    // on mobile when rendering several hundred icons on page
    const loadingRef = useRef(false);
    const unitRef = useRef();
    const loadedRef = useRef(false);
    const isNone = icon === '' || icon?.id === '';

    const onClickHandler = useCallback(() => {
      if (!isActive && !disabled && onClick) {
        if (flipEnvelope) {
          flipEnvelope(showEnvelopeFront);
        }

        onClick(icon, id);
      }
    }, [isActive, icon, id, onClick, disabled]);

    const finishedLoading = useCallback(() => {
      loadedRef.current = true;
      loadingRef.current.style.visibility = 'hidden';
      unitRef.current.classList.remove('icons-unit--hidden');
    }, []);

    const className = cx('icons-unit', {
      'icons-unit--clickable': onClick,
      'icons-unit--square': square,
      'icons-unit--active': isActive,
      'icons-unit--hidden': !loadedRef.current,
      'icons-unit--disabled': disabled,
      'icons-unit--outline': outline && !isActive,
    });
    const defaultVideoSrc = typeof icon === 'object' ? icon?.defaultSrc : icon;
    const isVideo = isAllowedVideoFormat(defaultVideoSrc);
    return (
      <div
        className="icons-unit__wrapper"
        style={{height, width, position: 'relative', overflow: isVideo ? undefined : 'hidden'}}
      >
        <div
          style={{height, width, visibility: loadedRef.current ? 'hidden' : 'visible'}}
          className="icons-unit icons-unit--loader"
          ref={loadingRef}
        >
          <span className="evt-spinner inline" />
        </div>
        {isVideo ? (
          <>
            {!hideAnimatedIndicator && (
              <SparkleIcon ratio={mobile ? 0.75 : 1} className="icons-unit__svg" />
            )}
            <Button
              variant="transparent"
              data-qa-id="icon-unit-video-selector"
              onClick={onClickHandler}
            >
              {typeof icon === 'string' ? (
                <Video
                  className={className}
                  style={{height, width, objectFit: 'fill'}}
                  onCanPlay={finishedLoading}
                  src={icon}
                  inViewAutoPlay
                  ref={unitRef}
                />
              ) : (
                <VideoAlphaChannel
                  className={className}
                  style={{height, width, objectFit: 'fill'}}
                  onCanPlay={finishedLoading}
                  srcVp9={icon?.iconVp9}
                  srcH265={icon?.iconH265}
                  inViewAutoPlay
                  ref={unitRef}
                />
              )}
            </Button>
          </>
        ) : (
          <Button
            variant="transparent"
            data-qa-id="icon-unit-img-selector"
            onClick={onClickHandler}
          >
            {isNone ? (
              <div className={cx('icons-unit__none', className)} style={{height, width}}>
                <Typography variant="paragraph4" color="pebble">
                  NONE
                </Typography>
              </div>
            ) : (
              <img
                className={className}
                style={{height, width}}
                src={icon}
                onLoad={finishedLoading}
                alt="Icon"
                loading={loading}
                width={width}
                height={height}
                ref={unitRef}
              />
            )}
          </Button>
        )}
      </div>
    );
  }
);
